/**
 * useProjects — fetch the authenticated user's full project list.
 *
 * Backed by `GET /api/v1/projects`, which returns `{ success, data, meta }`.
 * The endpoint already filters by org membership and excludes soft-deleted
 * rows. Sorting is `created_at desc` server-side; consumers that care about
 * recency (e.g. `useActiveProjectResolver`) sort by `updated_at` client-side.
 *
 * Cache: 5 min staleTime and `refetchOnWindowFocus: false` — the project
 * list rarely changes, and the cost of a stale list on the home page is at
 * most one render with the prior most-recent project. The explicit
 * `refetchOnWindowFocus: false` documents intent and stays correct if
 * global query defaults change.
 */

import { useQuery } from '@tanstack/react-query';
import { excludeDrafts } from '@shared/project-draft';
import { useAuth } from '@/hooks/useAuth';
import { useUserOrgs } from './useUserOrgs';

export interface ProjectListItem {
  id: string;
  short_id: string;
  name: string;
  status?: string;
  updated_at?: string;
  created_at?: string;
  // Tolerate other columns the endpoint returns.
  [key: string]: unknown;
}

/**
 * Statuses that don't belong in the active workspace list. The v1
 * `/api/v1/projects` endpoint only filters by `deleted_at IS NULL`, so we
 * trim archived rows here to keep them out of the v2 home picker, deep-link
 * validation, and project switcher. v1 `ProjectsPage` still calls the
 * endpoint directly and keeps its own UI for archived rows.
 */
const HIDDEN_STATUSES: ReadonlySet<string> = new Set(['archived']);

export interface UseProjectsResult {
  data: ProjectListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const FIVE_MINUTES = 5 * 60 * 1000;

export function useProjects(): UseProjectsResult {
  const { currentOrgId } = useUserOrgs();
  const { isAuthenticated } = useAuth();

  const query = useQuery<ProjectListItem[], Error>({
    queryKey: ['v2', 'projects', currentOrgId] as const,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated,
    // 401 = auth-init race (a settling cookie refresh) for an already-authenticated
    // user; `enabled` above stops the fetch entirely while logged out.
    // 403 = stale anaka_current_org_id from another env; the UserOrgsContext
    // self-heal effect runs on the next orgs fetch and corrects it, after which
    // the query key changes and a fresh fetch fires — so retry once here to give
    // the heal a chance before surfacing an error.
    retry: (failureCount, error) => {
      const status = (error as Error & { status?: number })?.status;
      return (status === 401 || status === 403) && failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 2000),
    queryFn: async () => {
      // `?include=home` adds a slim per-project `home` card (phase label +
      // recommendations) for the multi-project home (state 10); core fields are
      // unchanged. See server/services/home-project-card.ts.
      //
      // `?org_id=` scopes the result to the currently selected workspace so
      // switching org immediately shows only that org's projects (ANA-167).
      const orgParam = currentOrgId ? `&org_id=${encodeURIComponent(currentOrgId)}` : '';
      const res = await fetch(`/api/v1/projects?include=home${orgParam}`, { credentials: 'include' });
      if (!res.ok) {
        const err = new Error(`Project list fetch failed: ${res.status}`) as Error & {
          status?: number;
        };
        err.status = res.status;
        throw err;
      }
      const body = (await res.json()) as { success?: boolean; data?: ProjectListItem[] } | null;
      if (!body || !Array.isArray(body.data)) {
        throw new Error('Malformed response: missing data array');
      }
      // R5 (spec 041): unfinalised creation drafts (`status='created'` +
      // `metadata.creation.draft=true`) are owner-private and hidden. The server
      // list already excludes them (`notDraftClause`); `excludeDrafts` here is
      // belt-and-braces — a no-op on the metadata-less `?include=home` card, but
      // it holds if the server ever regresses or returns full rows. Centralized
      // in @shared/project-draft so the rule can't drift per-reader.
      return excludeDrafts(
        body.data.filter((p) => typeof p.status !== 'string' || !HIDDEN_STATUSES.has(p.status)),
      );
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
